/* ============================================================
   MAIN SEARCH
============================================================ */

if (!customElements.get('main-search')) {
  class MainSearch extends HTMLElement {
    constructor() {
      super();

      this.searchInput = this.querySelector('.main-search__input');
      this.resultsBox = this.querySelector('.main-search__results');
      this.closeButtons = this.querySelectorAll('.main-search__close');

      this.oldSearchValue = '';
      this.searchTimeoutID = null;
      this.searchAbortController = null;

      /*
       * Incremented for every new search.
       * Prevents older requests from replacing newer results.
       */
      this.searchRequestId = 0;

      this.searchDelay = 500;
      this.resultLimit = 9;

      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleClose = this.handleClose.bind(this);
    }


    /* ============================================================
       CONNECT
    ============================================================ */

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.initCloseButtons();
      this.initKeyboard();

      if (
        this.dataset.quickSearch === 'true' &&
        this.searchInput &&
        this.resultsBox
      ) {
        this.initQuickSearch();
      }
    }


    /* ============================================================
       DISCONNECT
    ============================================================ */

    disconnectedCallback() {
      this.cancelSearch();

      this.closeButtons.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleClose
        );
      });

      if (this.searchInput) {
        this.searchInput.removeEventListener(
          'keydown',
          this.handleKeydown
        );

        this.searchInput.removeEventListener(
          'input',
          this.handleInputChange
        );
      }

      this.dataset.initialized = 'false';
    }


    /* ============================================================
       CLOSE SEARCH
    ============================================================ */

    initCloseButtons() {
      this.closeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleClose
        );
      });
    }


    handleClose(event) {
      event.preventDefault();

      document.body.classList.remove('show-search');

      this.cancelSearch();
    }


    /* ============================================================
       KEYBOARD
    ============================================================ */

    initKeyboard() {
      if (!this.searchInput) return;

      this.searchInput.addEventListener(
        'keydown',
        this.handleKeydown
      );
    }


    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      event.preventDefault();

      document.body.classList.remove('show-search');

      this.cancelSearch();
    }


    /* ============================================================
       QUICK SEARCH
    ============================================================ */

    initQuickSearch() {
      /*
       * input handles:
       * - typing
       * - paste
       * - autofill
       * - mobile keyboards
       */
      this.searchInput.addEventListener(
        'input',
        this.handleInputChange
      );
    }


    /* ============================================================
       INPUT CHANGE
    ============================================================ */

    handleInputChange() {
      const searchValue = this.searchInput.value.trim();

      /*
       * Empty search.
       */
      if (!searchValue) {
        this.oldSearchValue = '';

        /*
         * Invalidates any request that may still finish.
         */
        this.searchRequestId++;

        this.cancelSearch();
        this.clearResults();

        return;
      }


      /*
       * Avoid repeating identical query.
       */
      if (searchValue === this.oldSearchValue) {
        return;
      }


      this.oldSearchValue = searchValue;

      /*
       * Cancel previous debounce/request.
       */
      this.cancelSearch();


      /*
       * Show loading UI.
       */
      this.setLoadingState();


      /*
       * Debounce predictive search.
       */
      this.searchTimeoutID = window.setTimeout(() => {
        this.performSearch(searchValue);
      }, this.searchDelay);
    }


    /* ============================================================
       BUILD SEARCH URLS
    ============================================================ */

    buildSearchUrls(searchValue) {
      const form = this.searchInput?.closest('form');

      if (!form) {
        return null;
      }

      /* ============================================================
        FULL SEARCH URL
        Keep the real value entered by the customer.
      ============================================================ */

      const linkURL = new URL(
        form.action,
        window.location.origin
      );

      const formParams = new URLSearchParams(
        new FormData(form)
      );

      formParams.set('q', searchValue);

      formParams.forEach((value, key) => {
        linkURL.searchParams.set(key, value);
      });


      /* ============================================================
        AJAX / PREDICTIVE SEARCH URL
      ============================================================ */

      let ajaxURL;

      const hasPredictiveSearch =
        theme?.Shopify?.features?.predictiveSearch;

      if (hasPredictiveSearch) {
        ajaxURL = new URL(
          theme.routes.base + theme.routes.predictiveSearch,
          window.location.origin
        );

        /*
        * Shopify currently gives us the desired Realtree
        * predictive results for "realtre", while the complete
        * "realtree" query ranks unrelated Black Camo products.
        *
        * Normalize only the predictive request.
        */
        const predictiveSearchValue =
          this.normalizePredictiveSearchValue(searchValue);

        ajaxURL.searchParams.set(
          'q',
          predictiveSearchValue
        );

        ajaxURL.searchParams.set(
          'section_id',
          'predictive-search'
        );

        ajaxURL.searchParams.set(
          'resources[limit]',
          String(this.resultLimit)
        );

        ajaxURL.searchParams.set(
          'resources[options][fields]',
          [
            'title',
            'product_type',
            'variants.title',
            'vendor',
            'tag',
            'variants.sku'
          ].join(',')
        );
      } else {
        ajaxURL = new URL(linkURL);

        ajaxURL.searchParams.set(
          'section_id',
          'main-search'
        );
      }

      return {
        ajaxURL,
        linkURL
      };
    }


    /* ============================================================
       PERFORM SEARCH
    ============================================================ */

    async performSearch(searchValue) {
      const urls = this.buildSearchUrls(searchValue);

      if (!urls) {
        this.clearResults();
        return;
      }


      /*
       * Every search gets its own unique ID.
       */
      const requestId = ++this.searchRequestId;


      /*
       * Use a LOCAL abort controller.
       *
       * This avoids an older request's finally() block
       * accidentally clearing the controller belonging
       * to a newer request.
       */
      const controller = new AbortController();

      this.searchAbortController = controller;


      const {
        ajaxURL,
        linkURL
      } = urls;


      try {
        const response = await fetch(
          ajaxURL.toString(),
          {
            method: 'GET',
            signal: controller.signal,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          }
        );


        if (!response.ok) {
          throw new Error(
            `Search request failed: ${response.status}`
          );
        }


        const html = await response.text();


        /*
         * IMPORTANT:
         * Do not allow an older request to render.
         *
         * Example:
         *
         * Request 1: realtre
         * Request 2: realtree
         *
         * If Request 1 finishes after Request 2,
         * it is discarded here.
         */
        if (requestId !== this.searchRequestId) {
          return;
        }


        /*
         * Also make sure the input hasn't changed.
         */
        const currentValue =
          this.searchInput?.value.trim() || '';

        if (currentValue !== searchValue) {
          return;
        }


        this.renderResults(
          html,
          linkURL
        );
      } catch (error) {
        /*
         * Aborted requests are expected while typing.
         */
        if (
          error.name === 'AbortError' ||
          controller.signal.aborted
        ) {
          return;
        }


        console.error(
          '[Main Search]',
          error
        );


        /*
         * Only the current request may change UI state.
         */
        if (requestId === this.searchRequestId) {
          this.showNoResults();
        }
      } finally {
        /*
         * Only clear state if this is still the newest request.
         */
        if (requestId === this.searchRequestId) {
          if (this.searchAbortController === controller) {
            this.searchAbortController = null;
          }

          this.classList.remove(
            'main-search--loading'
          );
        }
      }
    }


    /* ============================================================
       RENDER RESULTS
    ============================================================ */

    renderResults(html, linkURL) {
      if (!this.resultsBox) return;


      const template =
        document.createElement('template');

      template.innerHTML = html;


      const hasPredictiveSearch =
        theme?.Shopify?.features?.predictiveSearch;


      let resultsList = null;


      if (hasPredictiveSearch) {
        resultsList =
          template.content.querySelector(
            '.product-grid'
          );
      } else {
        resultsList =
          template.content.querySelector(
            '.section-search-template .product-grid'
          );
      }


      /*
       * Shopify returned no product-grid.
       */
      if (!resultsList) {
        this.showNoResults();
        return;
      }


      this.resetResultClasses();

      this.resultsBox.innerHTML = '';


      /* ==========================================================
         PRODUCTS
      ========================================================== */

      const resultsProducts =
        document.createElement('div');

      resultsProducts.className =
        'main-search__results__products collection-listing';


      const productGrid =
        document.createElement('div');

      productGrid.className =
        resultsList.className;


      resultsProducts.appendChild(
        productGrid
      );


      const productBlocks = Array.from(
        resultsList.querySelectorAll(
          '.product-block:not(.collection-block):not(.page-block)'
        )
      );


      let addedProducts = 0;


      for (const block of productBlocks) {
        /*
         * Exactly resultLimit products.
         */
        if (addedProducts >= this.resultLimit) {
          break;
        }


        /*
         * Skip RX products when Loop Returns is active.
         */
        if (
          document.body.classList.contains(
            'loop-returns-activated'
          ) &&
          block.dataset.isRx === 'true'
        ) {
          continue;
        }


        block.classList.add(
          'main-search-result'
        );


        /*
         * Remove Quick Buy button.
         */
        block
          .querySelectorAll(
            '.btn.quickbuy-toggle'
          )
          .forEach((button) => {
            button.remove();
          });


        /*
         * Remove Quick Buy functionality from remaining elements.
         */
        block
          .querySelectorAll(
            '.quickbuy-toggle'
          )
          .forEach((element) => {
            element.classList.remove(
              'quickbuy-toggle'
            );
          });


        productGrid.appendChild(block);

        addedProducts++;
      }


      /* ==========================================================
         PAGES
      ========================================================== */

      const resultsPages =
        document.createElement('div');

      resultsPages.className =
        'main-search__results__pages';


      resultsList
        .querySelectorAll(
          '.product-block.page-block'
        )
        .forEach((block) => {
          const sourceLink =
            block.querySelector('a');

          const sourceTitle =
            block.querySelector(
              '.page-block__title'
            );


          if (!sourceLink || !sourceTitle) {
            return;
          }


          const item =
            document.createElement('a');

          item.className =
            'main-search-result main-search-result--page';

          item.href = sourceLink.href;


          const text =
            document.createElement('div');

          text.className =
            'main-search-result__text';

          text.textContent =
            sourceTitle.textContent.trim();


          item.appendChild(text);

          resultsPages.appendChild(item);
        });


      /* ==========================================================
         CHECK RESULTS
      ========================================================== */

      const productResults =
        productGrid.querySelectorAll(
          '.main-search-result'
        );

      const pageResults =
        resultsPages.querySelectorAll(
          '.main-search-result'
        );


      const hasProducts =
        productResults.length > 0;

      const hasPages =
        pageResults.length > 0;


      if (!hasProducts && !hasPages) {
        this.showNoResults();
        return;
      }


      /* ==========================================================
         RESULTS STATE
      ========================================================== */

      this.classList.add(
        'main-search--has-results'
      );


      this.classList.toggle(
        'main-search--results-on-multiple-lines',
        productResults.length > 4
      );


      /* ==========================================================
         PRODUCT RESULTS
      ========================================================== */

      if (hasProducts) {
        this.resultsBox.appendChild(
          resultsProducts
        );
      }


      /* ==========================================================
         PAGE RESULTS
      ========================================================== */

      if (hasPages) {
        const heading =
          document.createElement('h6');

        heading.className =
          'main-search-result__heading';

        heading.textContent =
          theme?.strings?.generalSearchPages || 'Pages';


        resultsPages.insertAdjacentElement(
          'afterbegin',
          heading
        );


        this.resultsBox.appendChild(
          resultsPages
        );
      }


      /* ==========================================================
         VIEW ALL
      ========================================================== */

      const allLink =
        document.createElement('a');

      allLink.className =
        'main-search__results-all-link btn btn--primary';

      allLink.href =
        linkURL.toString();

      allLink.textContent =
        theme?.strings?.generalSearchViewAll ||
        'View All Search Results';


      this.resultsBox.appendChild(
        allLink
      );
    }


    /* ============================================================
       LOADING STATE
    ============================================================ */

    setLoadingState() {
      this.resetResultClasses();

      this.classList.add(
        'main-search--loading'
      );


      if (!this.resultsBox) return;


      this.resultsBox.innerHTML = `
        <div class="main-search__results-spinner">
          <div class="loading-spinner"></div>
        </div>
      `;
    }


    /* ============================================================
       NO RESULTS
    ============================================================ */

    showNoResults() {
      this.resetResultClasses();

      this.classList.remove(
        'main-search--loading'
      );

      this.classList.add(
        'main-search--no-results'
      );


      if (!this.resultsBox) return;


      this.resultsBox.innerHTML = '';


      const emptyMessage =
        document.createElement('div');

      emptyMessage.className =
        'main-search__empty-message';

      emptyMessage.textContent =
        theme?.strings?.generalSearchNoResultsWithoutTerms ||
        'No results found.';


      this.resultsBox.appendChild(
        emptyMessage
      );
    }


    /* ============================================================
       CLEAR RESULTS
    ============================================================ */

    clearResults() {
      this.resetResultClasses();

      this.classList.remove(
        'main-search--loading'
      );


      if (this.resultsBox) {
        this.resultsBox.innerHTML = '';
      }
    }


    /* ============================================================
       RESET RESULT CLASSES
    ============================================================ */

    resetResultClasses() {
      this.classList.remove(
        'main-search--has-results',
        'main-search--results-on-multiple-lines',
        'main-search--no-results'
      );
    }


    /* ============================================================
       CANCEL SEARCH
    ============================================================ */

    cancelSearch() {
      /*
       * Cancel debounce.
       */
      if (this.searchTimeoutID !== null) {
        window.clearTimeout(
          this.searchTimeoutID
        );

        this.searchTimeoutID = null;
      }


      /*
       * Cancel active network request.
       */
      if (this.searchAbortController) {
        this.searchAbortController.abort();

        this.searchAbortController = null;
      }
    }

    /* ============================================================
      NORMALIZE PREDICTIVE SEARCH QUERY
    ============================================================ */

    normalizePredictiveSearchValue(searchValue) {
      if (!searchValue) {
        return '';
      }

      return searchValue.replace(
        /\brealtree\b/gi,
        'realtre'
      );
    }
  }


  customElements.define(
    'main-search',
    MainSearch
  );
}